import {
    ChangeEvent,
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useEffect,
    useState
} from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    InputWithValidation,
    Switch,
    ValidationState,
    Tabs,
    Tab,
    Typography,
    Transaction,
    ListEdit,
    AlertsError
} from 'components';
import { Transformation } from 'types/transformation';
import { Template } from 'types/template';
import { TemplateDetails } from './CommonStyled';
import ApplicationList from './ApplicationList';
import { ApplicationListProps } from './ApplicationList';
import { useBlockNavigation } from 'providers';
import styled from 'styled-components';
import AssignedTransformations from './AssignedTransformations';
import RecordingSchedule from './RecordingSchedule';

export interface TemplateProps extends ApplicationListProps {
    template: Template;
    templateSetter: Dispatch<SetStateAction<Template>>;
    isDialog?: boolean;
    setHasError?: (value: SetStateAction<boolean>) => void;
}

const TabWrapper = styled.div``;

const IconWrapper = styled.div`
    margin-right: 10px;
    display: flex;
    justify-content: center;
`;

const DEFAULT_SCHEDULE_TIME = {
    fromHours: 8,
    fromMinutes: 0,
    toHours: 17,
    toMinutes: 0
};

const AbstractTemplate: FC<TemplateProps> = ({
    template,
    whitelist,
    blacklist,
    templateSetter,
    whitelistSetter,
    blacklistSetter,
    isReadOnly,
    isDialog = false,
    setHasError
}) => {
    const { t } = useTranslation();
    const { setIsNavigationBlocked } = useBlockNavigation();
    const [activeTab, setActiveTab] = useState(0);
    const handleTabChange = useCallback(
        (_: any, value: number) => setActiveTab(value),
        [setActiveTab]
    );
    const [transformationsToAssign, setTransformationsToAssign] = useState<
        Transformation[]
    >(template.transformations);

    const handleFieldChange = (
        event: ChangeEvent<HTMLInputElement>,
        propName: keyof Template
    ) => {
        setIsNavigationBlocked(true);

        const target = event.target;
        let value: string | number | boolean;
        // fixme
        switch (typeof template[propName]) {
            case 'string':
                value = target?.value;
                break;
            case 'number':
                value = Number(target?.value);
                break;
            case 'boolean':
                value = target?.checked;
                break;
            default:
                console.error('Prop type not supported');
        }
        templateSetter((prevState) => ({
            ...prevState,
            [propName]: value
        }));
    };

    useEffect(() => {
        templateSetter((prevState) => ({
            ...prevState,
            transformations: transformationsToAssign
        }));
    }, [transformationsToAssign, templateSetter]);

    const nameStateMap = {
        [ValidationState.Error]: {
            icon: (
                <span>
                    <AlertsError size={16} />
                </span>
            ),
            popper: {
                content: t('expressions.invalid_name_length')
            },
            inputProps: { isError: true }
        }
    };

    const validateName = (value: string) => {
        if (!value || !value.trim().length || value.length >= 200) {
            setHasError && setHasError(true);
            return ValidationState.Error;
        }

        setHasError && setHasError(false);
        return ValidationState.Success;
    };

    return (
        <Box m={isDialog ? 0 : 8}>
            <TemplateDetails>
                <InputWithValidation
                    stateMap={nameStateMap}
                    isDisabled={isReadOnly}
                    isFullWidth
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        handleFieldChange(event, 'name')
                    }
                    label={t('expressions.template_name')}
                    value={template.name}
                    validate={validateName}
                    isRequired={true}
                />
                <Switch
                    isDisabled={isReadOnly}
                    onChange={(event) => {
                        handleFieldChange(event, 'recordOnStart');
                    }}
                    isChecked={template.recordOnStart}
                    label={t(
                        'expressions.start_recording_when_assigned_to_recorder'
                    )}
                    asteriskPosition="left"
                />
                <Switch
                    isDisabled={isReadOnly}
                    onChange={(event) => {
                        handleFieldChange(event, 'recordExtendedElementData');
                    }}
                    isChecked={template.recordExtendedElementData}
                    label={t('expressions.record_extended_element_data')}
                    asteriskPosition="left"
                />
                <Switch
                    isDisabled={isReadOnly}
                    onChange={(event) => {
                        templateSetter({
                            ...template,
                            schedule: {
                                ...template.schedule,
                                enabled: event.target?.checked,
                                time: DEFAULT_SCHEDULE_TIME
                            }
                        });
                    }}
                    isChecked={template.schedule.enabled}
                    label={t('expressions.scheduled_recording')}
                    asteriskPosition="left"
                />
                <Switch
                    isDisabled={isReadOnly}
                    onChange={(event) => {
                        handleFieldChange(event, 'mergeDifferentHosts');
                    }}
                    isChecked={template.mergeDifferentHosts}
                    label={t('expressions.merge_hosts')}
                    asteriskPosition="left"
                />
                <RecordingSchedule
                    templateSetter={templateSetter}
                    template={template}
                />
            </TemplateDetails>
            <Tabs activeTab={activeTab} onTabChange={handleTabChange}>
                <Tab>
                    <IconWrapper>
                        <ListEdit size={20} />
                    </IconWrapper>
                    <Typography>{t('expressions.application_list')}</Typography>
                </Tab>
                <Tab>
                    <IconWrapper>
                        <Transaction size={20} />
                    </IconWrapper>
                    <Typography>{t('general.Transformations')}</Typography>
                </Tab>
            </Tabs>
            <TabWrapper>
                {activeTab === 0 && (
                    <ApplicationList
                        isReadOnly={isReadOnly}
                        blacklist={blacklist}
                        blacklistSetter={blacklistSetter}
                        whitelist={whitelist}
                        whitelistSetter={whitelistSetter}
                    />
                )}
                {activeTab === 1 && (
                    <AssignedTransformations
                        isReadOnly={isReadOnly}
                        setTransformationsToAssign={setTransformationsToAssign}
                        transformations={transformationsToAssign}
                        assignedTransformations={transformationsToAssign}
                    />
                )}
            </TabWrapper>
        </Box>
    );
};

export default AbstractTemplate;
